<template>
  <v-timeline-item
    v-if="conversion"
    dot-color="leadActivity"
    icon="mdi-file-document-edit"
    fill-dot
  >
    <v-expansion-panels
      class="expansion-panels"
      variant="accordion"
      flat
    >
      <v-expansion-panel>
        <v-expansion-panel-title>
          <v-row
            no-gutters
            align="center"
            class="d-flex justify-center"
            :class="{ 'flex-column': $vuetify.display.smAndDown }"
          >
            <v-col md="8">
              <div class="mb-2 text-subtitle-1">
                {{ $t('partial-sale.title-timeline') }}
              </div>
              <div
                v-if="conversion"
                class="text-grey"
              >
                {{ propertyTypeLabel }}
              </div>
            </v-col>
            <v-col
              class="pr-2 text-right text-grey"
              :class="{ 'text-start mt-2': $vuetify.display.smAndDown }"
              md="4"
            >
              {{ formatDate(data.createdAt) }}
            </v-col>
          </v-row>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-row v-if="conversion">
            <v-col md="6">
              <v-list density="compact">
                <v-list-item class="px-0">
                  <v-list-item-title>
                    {{ $t('life-annuity.conversion.labels.contact') }}:
                  </v-list-item-title>
                  <div class="text-grey text--darken-3 text-body-2">
                    {{ name }}

                    <div class="text-grey text--darken-3 text-body-2">
                      {{ phone }}
                    </div>
                  </div>
                </v-list-item>

                <v-list-item class="px-0">
                  <v-list-item-title>
                    {{ $t('life-annuity.conversion.labels.callback-time-of-day') }}:
                  </v-list-item-title>
                  <div class="text-grey text--darken-3 text-body-2">
                    {{ callbackTimeOfDay }}
                  </div>
                </v-list-item>

                <v-list-item class="px-0">
                  <v-list-item-title>{{ $t('labels.source') }}:</v-list-item-title>
                  <div class="text-grey text--darken-3 text-body-2">
                    {{ source }}
                  </div>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col md="3">
              <v-list density="compact">
                <v-list-item class="px-0">
                  <v-list-item-title>
                    {{ $t('life-annuity.conversion.labels.object-type') }}:
                  </v-list-item-title>
                  <div class="text-grey text--darken-3 text-body-2">
                    {{ propertyTypeLabel }}
                  </div>
                </v-list-item>
                <v-list-item class="px-0">
                  <v-list-item-title> {{ $t('labels.zip-full') }}: </v-list-item-title>
                  <div class="text-grey text--darken-3 text-body-2">
                    {{ property.zip }}
                  </div>
                </v-list-item>
                <v-list-item class="px-0">
                  <v-list-item-title
                    >{{ $t('life-annuity.conversion.labels.property-value') }}:</v-list-item-title
                  >
                  <div class="text-grey text--darken-3 text-body-2">
                    {{ formatPrice(property.value) }}
                  </div>
                </v-list-item>
                <v-list-item class="px-0">
                  <v-list-item-title
                    >{{ $t('life-annuity.conversion.labels.living-area') }}:</v-list-item-title
                  >
                  <div class="text-grey text--darken-3 text-body-2">
                    {{ formatArea(property.livingArea) }}
                  </div>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col md="3">
              <v-list density="compact">
                <v-list-item class="px-0">
                  <v-list-item-title
                    >{{ $t('life-annuity.conversion.labels.building-year') }}:</v-list-item-title
                  >
                  <div class="text-grey text--darken-3 text-body-2">
                    {{ property.buildingYear }}
                  </div>
                </v-list-item>
                <v-list-item class="px-0">
                  <v-list-item-title>
                    {{ $t('life-annuity.conversion.labels.garage-amount') }}:
                  </v-list-item-title>
                  <div class="text-grey text--darken-3 text-body-2">
                    {{ property.garageAmount }}
                  </div>
                </v-list-item>
                <v-list-item class="px-0">
                  <v-list-item-title>
                    {{ $t('life-annuity.conversion.labels.self-occupied') }}:
                  </v-list-item-title>
                  <div class="text-grey text--darken-3 text-body-2">
                    {{ property.selfOccupied ? $t('labels.yes') : $t('labels.no') }}
                  </div>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-timeline-item>
</template>

<script>
import { timeToLabelMap } from '@/lib/callbackTimeOfDay'
import { conversionSource } from '@/lib/conversionSource'
import { propertyTypes } from '@/lib/propertyTypes'
import areaMixin from '@/mixins/area'
import currencyMixin from '@/mixins/currency'

import CONVERSION from '../queries/Conversion.gql'

export default {
  mixins: [areaMixin, currencyMixin],
  props: {
    data: { type: Object, default: null },
  },
  data() {
    return {
      conversion: {
        personalData: {},
        partialSale: {},
      },
    }
  },
  computed: {
    property() {
      return this.conversion?.partialSale?.property || {}
    },
    propertyTypeLabel() {
      return propertyTypes[this.property?.type?.toUpperCase()]
    },
    name() {
      return `${this.conversion.personalData.firstname} ${this.conversion.personalData.lastname}`
    },
    phone() {
      return this.conversion.personalData.formattedPhone
    },
    callbackTimeOfDay() {
      if (!this.conversion) return
      return this.conversion.partialSale.callbackTimeOfDay
        .map((time) => this.$t(timeToLabelMap[time]))
        .join(', ')
    },
    source() {
      return this.conversion.utmParams?.source || conversionSource[this.conversion.origin] || ''
    },
  },
  methods: {},
  apollo: {
    conversion: {
      query: CONVERSION,
      variables() {
        return {
          conversionId: this.data.details[this.data.type].conversionId,
        }
      },
    },
  },
}
</script>

<style scoped>
.expansion-panels {
  margin: -24px -24px 0 -24px;
}
</style>
